import './Main.css';
import React, { useState, useEffect, useRef } from 'react';
import _ from 'lodash';
import axios from 'axios';
import queryString from 'query-string';
import { useParams, useLocation } from 'react-router-dom';
import { Outlet } from 'react-router-dom';

// MUI imports
import Box from '@mui/material/Box';
import CssBaseline from '@mui/material/CssBaseline';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Badge from '@mui/material/Badge';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import Switch from '@mui/material/Switch';
import TableContainer from '@mui/material/TableContainer';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import { green } from '@mui/material/colors';
import { useTheme, alpha } from '@mui/material/styles';

// FontAwesome imports
import { faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// Local import
import Sidebar from './Sidebar';
import Login from './Login';
import { getOpportunitiesFromServer, migrateOpportunity } from '../dashboard/utils/opportunity';
import { isOpportunityDirty } from '../dashboard/utils/dirtyOpportunities.js';
import { migrateToLCL, migrateToAirFreight, migrateOrgForms } from '../dashboard/utils/product.js';
import { migratePipelineStage } from '../dashboard/accounts/AccountFunnelPipeline.js';
import { getSuppliersFromServer } from '../dashboard/utils/suppliers';
import { getAccountsFromServer, hasUserDrivenActivity } from '../dashboard/utils/accounts';
import email from '../dashboard/utils/email';
import SidePane from '../dashboard/sidepane/SidePane';

function migrateTradePipeline(tradeline) {
    if (!tradeline.tags) return tradeline;
    let changed = false;
    const newTags = tradeline.tags.map(t => {
        if (t.label?.startsWith('Pipeline: ')) {
            const oldStage = t.label.substr(10);
            const newStage = migratePipelineStage(oldStage);
            if (newStage !== oldStage) { changed = true; return { ...t, label: `Pipeline: ${newStage}` }; }
        }
        return t;
    });
    return changed ? { ...tradeline, tags: newTags } : tradeline;
}

const LOADING_MESSAGES = [
    'Wrangling shipping containers…',
    'Sweet-talking the customs officer…',
    'Stacking pallets like it’s Tetris…',
    'Plotting the scenic route (just kidding, the fast one)…',
    'Counting twenty-foot equivalents…',
    'Convincing the crane to cooperate…',
    'Chasing down that one missing B/L…',
    'Bribing the seagulls for tailwind…',
    'Refueling the freight forwarders with coffee…',
    'Untangling the supply chain…',
];

function LoadingMessage() {
    const [index, setIndex] = useState(() => Math.floor(Math.random() * LOADING_MESSAGES.length));
    useEffect(() => {
        const id = setInterval(() => setIndex(prev => (prev + 1) % LOADING_MESSAGES.length), 2500);
        return () => clearInterval(id);
    }, []);
    return (
        <Typography variant='body2' sx={{ mt: 2, color: '#605DEC', fontStyle: 'italic', textAlign: 'center', maxWidth: '320px', minHeight: '20px' }}>
            {LOADING_MESSAGES[index]}
        </Typography>
    );
}

function OrganizationMain(props) {
    const theme = useTheme();
    const parsedQuery = queryString.parse(window.location.search) || {};
    const { orgId } = useParams();
    // Elariom brings its own navigation, so it gets the whole screen like ?onlyoutlet does.
    const fullScreen = /\/elariom(\/|$)/.test(useLocation().pathname);
    const [ org, setOrg ] = useState(null);
    const [ me, setMe ] = useState(null);
    const [ notifications, setNotifications ] = useState(null);
    const [ suppliers, setSuppliers] = useState(null);
    const [ accounts, setAccounts ] = useState(null);
    const [ allAccounts, setAllAccounts ] = useState(null);
    const [ opportunities, setOpportunities ] = useState(null);
    const [ opportunitiesCount, setOpportunitiesCount ] = useState(null);
    const [ suppliersCount, setSuppliersCount ] = useState(null);
    const [ accountsCount, setAccountsCount ] = useState(null);
    const [ tradelines, setTradelines ] = useState(null);
    const [ autoPricings, setAutoPricings ] = useState(null);
    const [ accesstoken, setAccesstoken ] = useState(sessionStorage.getItem('accesstoken') || (parsedQuery.accesstoken ? parsedQuery.accesstoken : null) || null);
    const [ mobileOpen, setMobileOpen ] = useState(false);
    const [ exchangeRates, setExchangeRates ] = useState(null);
    const [ seaPorts, setSeaPorts ] = useState(null);
    const [ unlocode, setUnlocode ] = useState(null);
    const [ reportConfiguration, setReportConfiguration ] = useState(null);
    const [ initError, setInitError ] = useState(false);
    const [ minimizedSearch, setMinimizedSearch ] = useState(true);
    const [ featureToggle, setFeatureToggle ] = useState({ ...DEFAULT_TOGGLES, ...(JSON.parse(localStorage.getItem('featureToggle')) || {}) });
    const [ featureToggleDialog, setFeatureToggleDialog ] = useState(false);
    const [ emailLoginInfo, setEmailLoginInfo ] = useState(null);
    const [ emailConversations, setEmailConversations ] = useState(null);
    const [ removedThreads, setRemovedThreads ] = useState([]);
    const [ minimizedSidebar, setMinimizedSidebar ] = useState(false);
    const [ opportunitiesResetRequested, setOpportunitiesResetRequested ] = useState(false);

    window.aaaa = emailLoginInfo;

    const drawerWidth = minimizedSidebar ? 60 : 240;

    email.setConversationsListener({ organization: org, listener: (e) => { 
        setEmailConversations(e);
        setRemovedThreads([]);
    } });
    email.setExpiredListener({ organization: org, listener: onEmailRefreshConnection });

    const onEmailRefreshConnectionRef = useRef(null);
    const onEmailForceRefreshConnectionRef = useRef(null);
    const onNotificationRefreshConnectionRef = useRef(null);
    const onOpportunitiesRefreshConnectionRef = useRef(null);
    const lastServerUpdateTimestampRef = useRef(0);

    function _setFeatureToggle(toggle) {
        setFeatureToggle(toggle);
        localStorage.setItem('featureToggle', JSON.stringify(toggle));
    }

    useEffect(() => {
        // Add a listener for a special keyboard stroke to enable the feature toggle dialog
        const listener = (e) => {
            if (e.ctrlKey && e.shiftKey && e.key === 'M') {
                setFeatureToggleDialog(true);
            }
        };
        window.addEventListener('keydown', listener);
        return () => window.removeEventListener('keydown', listener);
    }, []);


    useEffect(() => {
        if (!org) {
            window.document.body.classList.add('loading');
            document.documentElement.classList.add('loading');

            (async () => {
                try {
                    if (accesstoken) {
                        const { data: _org } = await axios(`/api/organizations/${orgId}?accesstoken=${accesstoken}`);
                        const { data: _me } = await axios(`/api/organizations/${_org.id}/me?accesstoken=${accesstoken}`);
                        setOrg(migrateOrgForms(_org));
                        setMe(_me);
                        window.me = _me;
                    } else {
                        const { data: _org } = await axios(`/api/organizations/${orgId}`);
                        setOrg(migrateOrgForms(_org));
                    }
                } catch (e) {
                    const { data: _org } = await axios(`/api/organizations/${orgId}`);
                    setOrg(migrateOrgForms(_org));
                }
            })();
        } else {
            window.document.body.classList.remove('loading');
            document.documentElement.classList.remove('loading');
            window.document.documentElement.style.backgroundColor = '';
            window.document.body.style.backgroundColor = '';
        }
    }, [org, orgId, accesstoken]);

    useEffect(() => {
        if ((!org) || (!accesstoken) || (!me)) return;
        (async () => {
            const _opportunities = await getOpportunitiesFromServer({ organizationId: org.id, me, accesstoken, since: 0, onProgress: setOpportunitiesCount });
            const migratedOpportunities = _opportunities.map(o => migrateToAirFreight(migrateToLCL(migrateOpportunity({ opportunity: o }))));
            setOpportunities(migratedOpportunities);
            // Track the last server update timestamp from the loaded opportunities
            const latestServerModified = _.max(migratedOpportunities.map(o => parseInt(o.modified) || 0)) || 0;
            lastServerUpdateTimestampRef.current = latestServerModified;
        })();
    }, [org, accesstoken, me])

    useEffect(() => {
        if ((org) && (accesstoken)) {
            (async () => {
                try {
                    window.document.body.classList.add('login');
                    document.documentElement.classList.add('login');

                    await Promise.all([
                        (async () => {
                            const { data: _notifications } = await axios(`/api/notifications/?accesstoken=${accesstoken}`);
                            setNotifications(_notifications);
                        })(),
                        (async () => {
                            const _suppliers = await getSuppliersFromServer({ organizationId: org.id, me, accesstoken, since: 0, onProgress: setSuppliersCount });
                            setSuppliers(_suppliers);
                        })(),
                        (async () => {
                            const __tFetch0 = performance.now();
                            const _accounts = await getAccountsFromServer({ organizationId: org.id, me, accesstoken, since: 0, onProgress: setAccountsCount });
                            const __tFetch1 = performance.now();
                            console.log(`[perf] getAccountsFromServer: ${(__tFetch1 - __tFetch0).toFixed(0)}ms, ${_accounts.length} accounts`);
                            window.__accountsArrivedAt = performance.now();
                            // Filter inactive accounts: drop CW1-only mirror accounts (no user-driven activity or opportunities).
                            setAllAccounts(_accounts);
                            const filterInactive = org.settings?.accounts?.filterInactiveAccounts;
                            if (filterInactive) {
                                let linkedAccountIds = new Set();
                                try {
                                    const _opps = await getOpportunitiesFromServer({ organizationId: org.id, me, accesstoken, since: 0 });
                                    _opps.forEach(o => { if (o.account?.id) linkedAccountIds.add(o.account.id); });
                                    const { data: _tradelines } = await axios(`/api/tradelines?organizationId=${org.id}&accesstoken=${accesstoken}`);
                                    (_tradelines || []).forEach(t => { if (t.accountId) linkedAccountIds.add(t.accountId); });
                                } catch (e) { console.error('[accounts filter] Error fetching opportunities/tradelines for filter:', e); }

                                const graceDays = org.settings?.accounts?.filterGraceDays || 0;
                                const graceMs = graceDays * 24 * 60 * 60 * 1000;
                                const now = Date.now();
                                const filtered = _accounts.filter(account => {
                                    // Grace period: skip filtering for recently created accounts
                                    if (graceMs > 0 && account.created && (now - account.created) < graceMs) return true;
                                    return hasUserDrivenActivity({ account, opportunityAccountIds: linkedAccountIds });
                                });
                                console.log(`[accounts filter] ${_accounts.length} → ${filtered.length} accounts (dropped ${_accounts.length - filtered.length})`);
                                setAccounts(filtered);
                            } else {
                                setAccounts(_accounts);
                            }
                        })(),
                        (async () => {
                            const { data: _seaPorts } = await axios(`/api/ports?&accesstoken=${accesstoken}`);
                            setSeaPorts(_(_seaPorts).filter(p => p.type === 'sea').map(p => p.port).value());
                        })(),
                        (async () => {
                            const { data: _exchangeRates } = await axios(`/api/opportunities/exchange-rates?organizationId=${org.id}&accesstoken=${accesstoken}`);
                            setExchangeRates(_exchangeRates);
                        })(),
                        (async () => {
                            const { data: _reportConfiguration } = await axios(`/api/reportconfiguration?organizationId=${org.id}&accesstoken=${accesstoken}`);
                            setReportConfiguration(_reportConfiguration || []);
                        })(),
                        (async () => {
                            const { data: _unlocode } = await axios('https://monada-storage.s3.us-east-1.amazonaws.com/code-list.csv');
                            const _unlocodeMap = _unlocode.split('\n').map(l => {
                                const [, codePart1, codePart2, text] = l.split(',');
                                return { code: `${codePart1}${codePart2}`, text };
                            }).filter(r => !!r.code && !!r.text);
                            _unlocodeMap.shift();

                            const { data: _seaPorts } = await axios(`/api/ports?&accesstoken=${accesstoken}`);
                            const customUnlocode = _(_seaPorts)
                                .filter(p => p.type === 'unlocode')
                                .map(p => ({ code: p.port.code, text: p.port.portname }))
                                .value();

                            // Remove duplicates by preferring customUnlocode over _unlocodeMap
                            const _unlocodeMapByCode = _.keyBy(_unlocodeMap, 'code');
                            const customUnlocodeByCode = _.keyBy(customUnlocode, 'code');
                            const mergedUnlocode = [
                                // All customUnlocode (if same code as _unlocodeMap, prefers this version)
                                ...Object.values(customUnlocodeByCode),
                                // All _unlocodeMap entries whose code is not present in customUnlocode
                                ...Object.values(_unlocodeMapByCode).filter(u => !(u.code in customUnlocodeByCode))
                            ];
                            setUnlocode(mergedUnlocode);
                        })(),
                        (async () => {
                            const { data: _tradelines } = await axios(`/api/tradelines?organizationId=${org.id}&accesstoken=${accesstoken}`);
                            setTradelines(_tradelines.map(migrateTradePipeline));
                        })(),
                        (async () => {
                            const { data: _autoPricings } = await axios(`/api/autopricings?organizationId=${org.id}&accesstoken=${accesstoken}`);
                            setAutoPricings(_autoPricings);
                        })(),
                    ])

                    setEmailLoginInfo(await email.refreshEmailConnection({ org, accesstoken }));

                    setInterval(() => {
                        if (onEmailForceRefreshConnectionRef.current) {
                            onEmailForceRefreshConnectionRef.current();
                        }
                    }, 1000 * 60 * 30);

                    setInterval(() => {
                        if (onNotificationRefreshConnectionRef.current) {
                            onNotificationRefreshConnectionRef.current();
                        }
                    }, 1000 * 30);

                    setInterval(() => {
                        if (onOpportunitiesRefreshConnectionRef.current) {
                            onOpportunitiesRefreshConnectionRef.current();
                        }
                    }, 1000 * 10);
                } catch (e) {
                    setInitError(true);
                }
            })();
        } 
    }, [org, orgId, accesstoken]);

    if (!org) {
        return (
            <Box>
                &nbsp;
                <Box sx={{ position: 'fixed', left: 'calc(50% - 60px)', top: 'calc(50% - 40px)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }} >
                    <img src='/monada-wordmark.png' alt='Monada' style={{ height: '44px', width: 'auto', marginBottom: '16px' }} />
                    <CircularProgress />
                    <LoadingMessage />
                </Box>
            </Box>
        );
    }

    if ((!accesstoken) || (!org) || (!org.forms) || (!me) || (initError)) {
        window.document.body.classList.add('login');
        document.documentElement.classList.add('login');

        return (
            <Login 
                org={org}
                onLogin={({ accesstoken, me: _me, org: _org }) => {
                    sessionStorage.setItem('accesstoken', accesstoken);
                    setAccesstoken(accesstoken);
                    setOrg(migrateOrgForms(_org));
                    setMe(_me);
                    window.me = _me;
                }}
                initError={initError}
                setInitError={setInitError}
            />
        );
    }

    if (!opportunities || !accounts || !tradelines || !seaPorts || !exchangeRates || !unlocode || !autoPricings) {
        return (
            <Box>
                &nbsp;
                <Box sx={{ position: 'fixed', left: 'calc(50% - 60px)', top: 'calc(50% - 40px)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }} >
                    <img src='/monada-wordmark.png' alt='Monada' style={{ height: '44px', width: 'auto', marginBottom: '16px' }} />
                    <CircularProgress />
                    <LoadingMessage />
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, position: 'fixed', bottom: '6px', right: '6px', background: 'rgba(255,255,255, 0.5)', padding: '6px', borderRadius: '6px' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant='caption' sx={{ width: '300px' }}>
                                Loading accounts:
                            </Typography>
                            {!accountsCount ? (
                                <CircularProgress size={12} />
                            ) : !accounts ? (
                                <CircularProgress size={12} variant='determinate' value={Math.min(accountsCount.count / accountsCount.total, 1) * 100} />
                            ) : (
                                <FontAwesomeIcon style={{ fontSize: '12px' }} icon={faCheckCircle} color={green[500]}/>
                            )}
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant='caption' sx={{ width: '300px' }}>
                                Loading suppliers:
                            </Typography>
                            {!suppliersCount ? (
                                <CircularProgress size={12} />
                            ) : !suppliers ? (
                                <CircularProgress size={12} variant='determinate' value={Math.min(suppliersCount.count / suppliersCount.total, 1) * 100} />
                            ) : (
                                <FontAwesomeIcon style={{ fontSize: '12px' }} icon={faCheckCircle} color={green[500]}/>
                            )}
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant='caption' sx={{ width: '300px' }}>
                                Loading tradelines:
                            </Typography>
                            {!tradelines ? (
                                <CircularProgress size={12} />
                            ) : (
                                <FontAwesomeIcon style={{ fontSize: '12px' }} icon={faCheckCircle} color={green[500]}/>
                            )}
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant='caption' sx={{ width: '300px' }}>
                                Loading auto-pricings:
                            </Typography>
                            {!autoPricings ? (
                                <CircularProgress size={12} />
                            ) : (
                                <FontAwesomeIcon style={{ fontSize: '12px' }} icon={faCheckCircle} color={green[500]}/>
                            )}
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant='caption' sx={{ width: '300px' }}>
                                Loading sea ports & unlocodes:
                            </Typography>
                            {!seaPorts || !unlocode ? (
                                <CircularProgress size={12} />
                            ) : (
                                <FontAwesomeIcon style={{ fontSize: '12px' }} icon={faCheckCircle} color={green[500]}/>
                            )}
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant='caption' sx={{ width: '300px' }}>
                                Loading exchange rates:
                            </Typography>
                            {!exchangeRates ? (
                                <CircularProgress size={12} />
                            ) : (
                                <FontAwesomeIcon style={{ fontSize: '12px' }} icon={faCheckCircle} color={green[500]}/>
                            )}
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant='caption' sx={{ width: '300px' }}>
                                Loading opportunities:
                            </Typography>
                            {!opportunitiesCount ? (
                                <CircularProgress size={12} />
                            ) : !opportunities ? (
                                <CircularProgress size={12} variant='determinate' value={Math.min(opportunitiesCount.count / opportunitiesCount.total, 1) * 100} />
                            ) : (
                                <FontAwesomeIcon style={{ fontSize: '12px' }} icon={faCheckCircle} color={green[500]}/>
                            )}
                        </Box>
                    </Box>
                </Box>
            </Box>
        );
    }

    window.document.body.classList.remove('login');
    document.documentElement.classList.remove('login');

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    function onUpdateOpportunity({ opportunity, func, opportunityId }) {
        if (func) {
            setOpportunities(opportunities => {
                const op = opportunities.find(o => o.id === opportunityId);
                const newOp = func(op);
                return _.compact([ ...opportunities.filter(o => o.id !== opportunityId), newOp ]);
            });
        } else {
            setOpportunities(opportunities => {
                return [ ...opportunities.filter(o => o.id !== opportunity.id), opportunity ];
            });
        }
    }

    function onUpdateTradeline({ tradeline, delete: _delete }) {
        const t = tradelines.filter(t => t.id !== tradeline.id);
        if (_delete) {
            setTradelines(t);
        } else {
            setTradelines([ ...t, tradeline ]);
        }
    }

    function onUpdateAutoPricing({ autoPricing }) {
        const ap = autoPricings.filter(ap => ap.id !== autoPricing.id);
        setAutoPricings([ ...ap, autoPricing ]);
    }

    async function onEmailRefreshConnection() {
        console.log('[Main] onEmailRefreshConnection');
        setEmailLoginInfo(await email.refreshEmailConnection({ org, accesstoken }));
    }
    onEmailRefreshConnectionRef.current = onEmailRefreshConnection;

    async function onEmailForceRefreshConnection() {
        if (!emailLoginInfo || emailLoginInfo.status !== 'connected') return;
        console.log('[Main] onEmailForceRefreshConnection - forcing token refresh');
        try {
            setEmailLoginInfo(await email.forceRefreshEmailConnection({ org, accesstoken }));
        } catch (e) {}
    }
    onEmailForceRefreshConnectionRef.current = onEmailForceRefreshConnection;

    async function onNotificationRefreshConnection() {
        try {
            const { data: _notifications } = await axios(`/api/notifications?accesstoken=${accesstoken}`);
            if (!_.isEqual(_notifications, notifications)) {
                setNotifications(_notifications);
            }
        } catch (e) {}
    }
    onNotificationRefreshConnectionRef.current = onNotificationRefreshConnection;

    async function onOpportunitiesRefreshConnection() {
        // Only poll if opportunities have been loaded (indicated by timestamp > 0)
        if (lastServerUpdateTimestampRef.current === 0 || !org || !accesstoken || !me) return;
        
        try {
            // Use the last server update timestamp (not local changes)
            const since = lastServerUpdateTimestampRef.current + 1;

            // Fetch only opportunities modified since the last server update using getOpportunitiesFromServer
            const updatedItems = await getOpportunitiesFromServer({ 
                organizationId: org.id, 
                me, 
                accesstoken, 
                since 
            });

            // If there are new/updated opportunities, merge them with existing ones
            if (updatedItems && updatedItems.length > 0) {
                const updatedOpportunities = updatedItems.map(o => migrateToAirFreight(migrateToLCL(migrateOpportunity({ opportunity: o }))));
                
                // Track the latest server modified timestamp from the received updates
                const latestServerModified = _.max(updatedOpportunities.map(o => parseInt(o.modified) || 0)) || 0;
                if (latestServerModified > lastServerUpdateTimestampRef.current) {
                    lastServerUpdateTimestampRef.current = latestServerModified;
                }
                
                setOpportunities(prevOpportunities => {
                    // Create a map of current opportunities by ID for quick lookup
                    const opportunitiesMap = new Map(prevOpportunities.map(o => [o.id, o]));
                    
                    // Only update opportunities that have actually changed (newer modified value)
                    updatedOpportunities.forEach(updatedOp => {
                        // Never overwrite an opportunity the user is actively editing —
                        // it has unsaved local edits, and a poll snapshot would make
                        // just-added sections/fields disappear.
                        if (isOpportunityDirty(updatedOp.id)) return;

                        const existingOp = opportunitiesMap.get(updatedOp.id);
                        const updatedModified = parseInt(updatedOp.modified) || 0;
                        const existingModified = existingOp ? (parseInt(existingOp.modified) || 0) : 0;

                        // Only update if this is a new opportunity or the server version is newer
                        if (!existingOp || updatedModified > existingModified) {
                            opportunitiesMap.set(updatedOp.id, updatedOp);
                        }
                    });
                    
                    return Array.from(opportunitiesMap.values());
                });
            }
            
        } catch (e) {
            // If reset is requested, show banner to refresh the page
            if (e.message === 'Reset requested') {
                console.warn('[Main] Server requested local DB reset during opportunities polling');
                setOpportunitiesResetRequested(true);
            } else {
                console.error('[Main] Error refreshing opportunities:', e);
            }
        }
    }
    onOpportunitiesRefreshConnectionRef.current = onOpportunitiesRefreshConnection;

    async function onEmailLogout() {
        setEmailLoginInfo(null);
        await email.disconnect({ org, accesstoken });
        return onEmailRefreshConnection();
    }

    function onUpdateNotification({ notificationId, changes }) {
        setNotifications(prev => prev.map(n => n.id === notificationId ? { ...n, ...changes } : n));
    }


    const container = window.document.body;

    const DrawerIcon = (
        <Badge badgeContent={0} color='error' sx={{ mr: 2, display: { md: 'none' } }}>
            <IconButton
                color='inherit'
                aria-label='open drawer'
                edge='start'
                onClick={handleDrawerToggle}
            >
                <MenuIcon />
            </IconButton>
        </Badge>
    );

    if (parsedQuery.onlyoutlet || fullScreen) {
        return (
            <Box
                component='main'
                sx={{ flexGrow: 1, p: 0, width: '100%' }}
            >
                {opportunitiesResetRequested && (
                    <Box sx={{ position: 'sticky', top: '0px', zIndex: 1000, background: alpha(theme.palette.background.paper, 0.95), padding: '6px 12px' }}>
                        <Alert 
                            severity='warning' 
                            sx={{ margin: '12px 0' }}
                            action={
                                <Button
                                    size='small'
                                    variant='contained'
                                    color='warning'
                                    onClick={() => window.location.reload()}
                                >
                                    Refresh
                                </Button>
                            }
                        >
                            <AlertTitle>Data Refresh Required</AlertTitle>
                            The server has requested a data refresh. Please click Refresh to reload the page.
                        </Alert>
                    </Box>
                )}
                <Outlet context={{
                    accesstoken,
                    email: me.email, 
                    me, 
                    org,
                    onOrgUpdated: (org) => setOrg(migrateOrgForms(org)),
                    notifications,
                    onUpdateNotification,
                    DrawerIcon,
                    accounts,
                    allAccounts,
                    setAccounts,
                    suppliers,
                    setSuppliers,
                    opportunities,
                    setOpportunities,
                    onUpdateOpportunity,
                    tradelines,
                    onUpdateTradeline,
                    autoPricings,
                    onUpdateAutoPricing,
                    seaPorts,
                    unlocode,
                    minimizedSearch,
                    setMinimizedSearch,
                    exchangeRates,
                    reportConfiguration,
                    featureToggle,
                    emailLoginInfo,
                    onEmailRefreshConnection,
                    onEmailLogout,
                    drawerWidth,
                }} />
            </Box>
        );
    }

    return (
        <Box sx={{ display: 'flex' }}>
            <CssBaseline />
            <Box
                component='nav'
                sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
                aria-label='mailbox folders'
            >
            {/* The implementation can be swapped with js to avoid SEO duplication of links. */}
                <Drawer
                    container={container}
                    variant='temporary'
                    open={mobileOpen}
                    onClose={handleDrawerToggle}
                    ModalProps={{
                        keepMounted: true, // Better open performance on mobile.
                    }}
                    sx={{
                        display: { xs: 'block', md: 'none' }, 
                        '& .MuiDrawer-paper': { 
                            boxSizing: 'border-box', 
                            width: drawerWidth,
                            background: 'black',
                            color: 'white',
                        },
                    }}
                >
                    <Sidebar
                        org={org}
                        me={me}
                        accesstoken={accesstoken}
                        notifications={notifications}
                        emailConversations={emailConversations}
                        removedThreads={removedThreads}
                        setRemovedThreads={setRemovedThreads}
                        onUpdateNotification={onUpdateNotification}
                        opportunities={opportunities}
                        accounts={accounts}
                        suppliers={suppliers}
                        tradelines={tradelines}
                        onLogout={() => {
                            sessionStorage.removeItem('accesstoken');
                            setAccesstoken(null);
                            setOrg(null);
                        }}
                        featureToggle={featureToggle}
                        emailLoginInfo={emailLoginInfo}
                        onEmailLogout={onEmailLogout}
                        onEmailRefreshConnection={onEmailRefreshConnection}
                        minimized={minimizedSidebar}
                        setMinimized={setMinimizedSidebar}
                    />
                </Drawer>
                <Drawer
                    variant='permanent'
                    sx={{
                        display: { xs: 'none', md: 'block' },
                        '& .MuiDrawer-paper': { 
                            boxSizing: 'border-box', 
                            width: drawerWidth,
                            background: 'black',
                            color: 'white'
                        },
                    }}
                    open
                >
                    <Sidebar
                        org={org}
                        me={me}
                        accesstoken={accesstoken}
                        notifications={notifications}
                        emailConversations={emailConversations}
                        removedThreads={removedThreads}
                        setRemovedThreads={setRemovedThreads}
                        onUpdateNotification={onUpdateNotification}
                        opportunities={opportunities}
                        accounts={accounts}
                        suppliers={suppliers}
                        tradelines={tradelines}
                        onLogout={() => {
                            sessionStorage.removeItem('accesstoken');
                            setAccesstoken(null);
                            setOrg(null);
                        }}
                        featureToggle={featureToggle}
                        emailLoginInfo={emailLoginInfo}
                        onEmailLogout={onEmailLogout}
                        onEmailRefreshConnection={onEmailRefreshConnection}
                        minimized={minimizedSidebar}
                        setMinimized={setMinimizedSidebar}
                    />
                </Drawer>
            </Box>
            <Box
                component='main'
                sx={{ flexGrow: 1, p: 0, width: { md: `calc(100% - ${drawerWidth}px)` }, paddingRight: '37px' }}
            >
                {opportunitiesResetRequested && (
                    <Box sx={{ position: 'sticky', top: '0px', zIndex: 1000, background: alpha(theme.palette.background.paper, 0.95), padding: '6px 12px' }}>
                        <Alert 
                            severity='warning' 
                            sx={{ margin: '12px 0' }}
                            action={
                                <Button
                                    size='small'
                                    variant='contained'
                                    color='warning'
                                    onClick={() => window.location.reload()}
                                >
                                    Refresh
                                </Button>
                            }
                        >
                            <AlertTitle>Data Refresh Required</AlertTitle>
                            The server has requested a data refresh. Please click Refresh to reload the page.
                        </Alert>
                    </Box>
                )}
                <Outlet context={{
                    accesstoken,
                    email: me.email, 
                    me, 
                    org,
                    onOrgUpdated: (org) => setOrg(migrateOrgForms(org)),
                    notifications,
                    onUpdateNotification,
                    DrawerIcon,
                    accounts,
                    allAccounts,
                    setAccounts,
                    suppliers,
                    setSuppliers,
                    opportunities,
                    setOpportunities,
                    onUpdateOpportunity,
                    tradelines,
                    onUpdateTradeline,
                    autoPricings,
                    onUpdateAutoPricing,
                    seaPorts,
                    unlocode,
                    minimizedSearch,
                    setMinimizedSearch,
                    exchangeRates,
                    reportConfiguration,
                    featureToggle,
                    emailLoginInfo,
                    emailConversations,
                    removedThreads,
                    setRemovedThreads,
                    onEmailRefreshConnection,
                    onEmailLogout,
                    drawerWidth,
                }} />
            </Box>
            <SidePane 
                minimizedSearch={minimizedSearch} 
                setMinimizedSearch={setMinimizedSearch} 
                accesstoken={accesstoken} 
                org={org} 
                exchangeRates={exchangeRates} 
                seaPorts={seaPorts} 
                unlocode={unlocode} 
                opportunities={opportunities} 
                onUpdateOpportunity={onUpdateOpportunity} 
                emailLoginInfo={emailLoginInfo}
                me={me}
                suppliers={suppliers}
                accounts={accounts}
                setAccounts={setAccounts}
                setSuppliers={setSuppliers}
                featureToggle={featureToggle}
            />
            <Dialog fullWidth={true} maxWidth='lg' open={featureToggleDialog} onClose={() => setFeatureToggleDialog(false)}>
                <DialogTitle>Feature Toggles</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Feature</TableCell>
                                        <TableCell>Toggle</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {FEATURE_TOGGLES.map(toggle => (
                                        <TableRow key={toggle}>
                                            <TableCell>
                                                <Typography variant='body2'>{toggle}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Switch
                                                    checked={featureToggle[toggle]}
                                                    onChange={(e) => _setFeatureToggle({ ...featureToggle, [toggle]: e.target.checked })}
                                                />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <Button
                            variant='contained'
                            color='primary'
                            onClick={() => _setFeatureToggle(DEFAULT_TOGGLES)}
                            sx={{ mt: 2 }}
                        >
                            Reset to Default
                        </Button>
                    </DialogContentText>
                </DialogContent>
            </Dialog>
        </Box>
    );
}

const FEATURE_TOGGLES = [
    'page.companyfeed',
    'page.account-create-opportunity',
    'feature.campaigns-and-audiences',
    'feature.account-email',
    'feature.shipments',
    'feature.advancedsearch',
    'feature.accountsendtocw',
    'feature.accountadvancedsorting',
    'feature.accountimportleads',
    'feature.account-kpis',
    'feature.darkmode',
    'feature.opportunities-kanban',
    'feature.accounts-hide-new',
];

const DEFAULT_TOGGLES = {
    'page.companyfeed': true,
    'page.account-create-opportunity': false,
    'feature.campaigns-and-audiences': false,
    'feature.account-email': false,
    'feature.shipments': false,
    'feature.advancedsearch': false,
    'feature.accountsendtocw': false,
    'feature.accountadvancedsorting': false,
    'feature.accountimportleads': false,
    'feature.account-kpis': false,
    'feature.opportunities-kanban': false,
    'feature.accounts-hide-new': false,
};

export default OrganizationMain;
